import React from 'react';
import {
  Box,
  Container,
  Typography,
  Avatar,
  Stack,
  Chip,
  Button,
  Divider,
  IconButton,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Breadcrumbs,
  Link,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Share,
  ArrowBack,
  LinkedIn,
  Twitter,
  GitHub,
  Language,
  Bookmark,
  Home,
  NavigateNext,
  Article,
  TrendingUp,
  Visibility,
  ContentCopy,
} from '@mui/icons-material';
import { Link as RouterLink, useParams, Navigate } from 'react-router-dom';
import { getBlogPostBySlug, authors, getLatestPosts } from '../data/blogPosts';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark, oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { useTheme } from '@mui/material/styles';
import { generateSEOTags, applySEOTags } from '../utils/seoUtils';

const MarkdownComponents = (theme) => ({
  h1: ({ children }) => (
    <Typography 
      variant="h3" 
      component="h1" 
      sx={{ 
        mb: 4, 
        mt: 6,
        fontWeight: 700, 
        lineHeight: 1.2,
      }}
    >
      {children}
    </Typography>
  ),
  h2: ({ children }) => (
    <Typography 
      variant="h4" 
      component="h2" 
      sx={{ 
        mt: 6, 
        mb: 3, 
        fontWeight: 600, 
        borderLeft: '4px solid',
        borderColor: 'primary.main',
        pl: 2,
      }}
    >
      {children}
    </Typography>
  ),
  h3: ({ children }) => (
    <Typography 
      variant="h5" 
      component="h3" 
      sx={{ 
        mt: 4, 
        mb: 2, 
        fontWeight: 600, 
      }}
    >
      {children}
    </Typography>
  ),
  h4: ({ children }) => (
    <Typography 
      variant="h6" 
      component="h4" 
      sx={{ 
        mt: 3, 
        mb: 2, 
        fontWeight: 600, 
      }}
    >
      {children}
    </Typography>
  ),
  p: ({ children }) => (
    <Typography 
      variant="body1" 
      sx={{ 
        mb: 3, 
        lineHeight: 1.8,
      }}
    >
      {children}
    </Typography>
  ),
  strong: ({ children }) => (
    <Typography 
      component="strong" 
      sx={{ 
        fontWeight: 700, 
      }}
    >
      {children}
    </Typography>
  ),
  em: ({ children }) => (
    <Typography 
      component="em" 
      sx={{ 
        fontStyle: 'italic',
        color: 'text.secondary',
      }}
    >
      {children}
    </Typography>
  ),
  ul: ({ children }) => (
    <Box component="ul" sx={{ mb: 3, pl: 3 }}>
      {children}
    </Box>
  ),
  ol: ({ children }) => (
    <Box component="ol" sx={{ mb: 3, pl: 3 }}>
      {children}
    </Box>
  ),
  li: ({ children }) => (
    <Typography component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
      {children}
    </Typography>
  ),
  blockquote: ({ children }) => (
    <Paper
      elevation={0}
      sx={{
        borderLeft: '4px solid',
        borderColor: 'secondary.main',
        backgroundColor: 'action.hover',
        p: 3,
        my: 3,
        borderRadius: '0 8px 8px 0',
        fontStyle: 'italic',
      }}
    >
      {children}
    </Paper>
  ),
  table: ({ children }) => (
    <Box sx={{ overflowX: 'auto', mb: 3 }}>
      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
        <Box
          component="table"
          sx={{
            width: '100%',
            borderCollapse: 'collapse',
            '& th': {
              backgroundColor: 'primary.main',
              color: 'primary.contrastText',
              p: 2,
              fontWeight: 600,
              textAlign: 'left',
              borderBottom: '1px solid',
              borderColor: 'divider',
            },
            '& td': {
              p: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
            },
            '& tr:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          {children}
        </Box>
      </Paper>
    </Box>
  ),
  code: ({ node, inline, className, children, ...props }) => {
    const match = /language-(\\w+)/.exec(className || '');
    const language = match ? match[1] : 'text';
    const codeContent = String(children).replace(/\n$/, '');
    
    return !inline && (match || codeContent.includes('\n')) ? (
      <Box sx={{ my: 4, position: 'relative' }}>
        {/* Enhanced Code Block Container */}
        <Box 
          sx={{ 
            borderRadius: 3,
            overflow: 'hidden',
            backgroundColor: theme.palette.mode === 'dark' ? '#0d1117' : '#f8fafc',
            border: '1px solid',
            borderColor: theme.palette.mode === 'dark' ? 'rgba(56, 139, 253, 0.15)' : 'rgba(13, 110, 253, 0.1)',
            boxShadow: theme.palette.mode === 'dark' 
              ? '0 8px 32px rgba(0, 0, 0, 0.4), 0 1px 3px rgba(0, 0, 0, 0.3)'
              : '0 8px 32px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.06)',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '1px',
              background: theme.palette.mode === 'dark' 
                ? 'linear-gradient(90deg, transparent, rgba(56, 139, 253, 0.3), transparent)'
                : 'linear-gradient(90deg, transparent, rgba(13, 110, 253, 0.2), transparent)',
            }
          }}
        >
          {/* Header Bar */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 3,
              py: 1.5,
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(22, 27, 34, 0.8)' : 'rgba(248, 250, 252, 0.8)',
              borderBottom: '1px solid',
              borderColor: theme.palette.mode === 'dark' ? 'rgba(56, 139, 253, 0.1)' : 'rgba(13, 110, 253, 0.08)',
              backdropFilter: 'blur(8px)',
            }}
          >
            {/* Terminal Dots */}
            <Stack direction="row" spacing={1}>
              <Box sx={{ 
                width: 12, 
                height: 12, 
                borderRadius: '50%', 
                backgroundColor: '#ff5f57' 
              }} />
              <Box sx={{ 
                width: 12, 
                height: 12, 
                borderRadius: '50%', 
                backgroundColor: '#ffbd2e' 
              }} />
              <Box sx={{ 
                width: 12, 
                height: 12, 
                borderRadius: '50%', 
                backgroundColor: '#28ca42' 
              }} />
            </Stack>
            
            {/* Language Badge */}
            <Chip
              label={language.toUpperCase()}
              size="small"
              sx={{
                backgroundColor: theme.palette.mode === 'dark' 
                  ? 'rgba(56, 139, 253, 0.15)' 
                  : 'rgba(13, 110, 253, 0.1)',
                color: theme.palette.mode === 'dark' 
                  ? '#58a6ff' 
                  : '#0969da',
                fontSize: '0.6875rem',
                fontWeight: 600,
                height: 24,
                border: '1px solid',
                borderColor: theme.palette.mode === 'dark' 
                  ? 'rgba(56, 139, 253, 0.2)' 
                  : 'rgba(13, 110, 253, 0.15)',
              }}
            />
          </Box>
          
          {/* Code Content */}
          <Box sx={{ position: 'relative' }}>
            <SyntaxHighlighter
              style={theme.palette.mode === 'dark' ? oneDark : oneLight}
              language={language}
              PreTag="div"
              showLineNumbers={false}
              wrapLines={true}
              wrapLongLines={true}
              customStyle={{
                margin: 0,
                padding: '1.5rem',
                backgroundColor: 'transparent',
                fontSize: '0.875rem',
                lineHeight: 1.7,
                fontFamily: '"Fira Code", "JetBrains Mono", "SF Mono", Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                overflow: 'auto',
                maxHeight: '500px',
              }}
              codeTagProps={{
                style: {
                  fontSize: '0.875rem',
                  fontFamily: '"Fira Code", "JetBrains Mono", "SF Mono", Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                }
              }}
              {...props}
            >
              {codeContent}
            </SyntaxHighlighter>
            
            {/* Copy Button */}
            <IconButton
              size="small"
              onClick={() => navigator.clipboard?.writeText(codeContent)}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                backgroundColor: theme.palette.mode === 'dark' 
                  ? 'rgba(22, 27, 34, 0.8)' 
                  : 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(8px)',
                border: '1px solid',
                borderColor: theme.palette.mode === 'dark' 
                  ? 'rgba(56, 139, 253, 0.2)' 
                  : 'rgba(13, 110, 253, 0.15)',
                color: theme.palette.mode === 'dark' ? '#58a6ff' : '#0969da',
                opacity: 0.7,
                transition: 'all 0.2s ease',
                '&:hover': {
                  opacity: 1,
                  backgroundColor: theme.palette.mode === 'dark' 
                    ? 'rgba(56, 139, 253, 0.1)' 
                    : 'rgba(13, 110, 253, 0.08)',
                  transform: 'scale(1.05)',
                },
              }}
            >
              <ContentCopy sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        </Box>
      </Box>
    ) : (
      // Enhanced Inline Code
      <Box
        component="code"
        sx={{
          backgroundColor: theme.palette.mode === 'dark' 
            ? 'rgba(56, 139, 253, 0.15)' 
            : 'rgba(13, 110, 253, 0.08)',
          color: theme.palette.mode === 'dark' 
            ? '#58a6ff' 
            : '#0969da',
          px: 0.875,
          py: 0.375,
          borderRadius: 1,
          fontSize: '0.875em',
          fontFamily: '"Fira Code", "JetBrains Mono", "SF Mono", Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
          fontWeight: 500,
          border: '1px solid',
          borderColor: theme.palette.mode === 'dark' 
            ? 'rgba(56, 139, 253, 0.2)' 
            : 'rgba(13, 110, 253, 0.15)',
          boxShadow: theme.palette.mode === 'dark' 
            ? '0 1px 3px rgba(0, 0, 0, 0.3)' 
            : '0 1px 3px rgba(0, 0, 0, 0.08)',
        }}
        {...props}
      >
        {children}
      </Box>
    );
  },
  hr: () => (
    <Divider sx={{ my: 4 }} />
  ),
});

const BlogPostPage = () => {
  const theme = useTheme();
  const { slug } = useParams();
  const post = getBlogPostBySlug(slug);
  const relatedPosts = getLatestPosts(4).filter(p => p.slug !== slug);


  // SEO meta tags using utility
  React.useEffect(() => {
    if (post) {
      const seoTags = generateSEOTags({
        title: post.title,
        description: post.metaDescription || post.excerpt,
        keywords: post.keywords || [],
        image: post.socialImage || post.ogImage, // Use socialImage for sharing, ogImage for blog header
        url: post.canonicalUrl || window.location.href,
        type: 'article',
        publishedTime: post.date.toISOString(),
        modifiedTime: post.date.toISOString(),
        author: 'Kief Studio',
        siteName: 'PWGen-Rust'
      });
      
      applySEOTags(seoTags);
    }
    
    return () => {
      // Reset title on cleanup
      document.title = 'PWGen-Rust - Secure Password Manager';
    };
  }, [post]);


  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getReadingTime = (post) => {
    if (post.readingTime) return post.readingTime;
    const wordsPerMinute = 200;
    const words = post.content.split(' ').length;
    return Math.ceil(words / wordsPerMinute);
  };

  const handleShare = async () => {
    const shareData = {
      title: post.title,
      text: post.excerpt,
      url: window.location.href,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        console.log('Error sharing:', err);
      }
    } else {
      await navigator.clipboard.writeText(window.location.href);
    }
  };


  return (
    <Box>
      {/* Hero Image Section */}
      {post.ogImage && (
        <Box
          sx={{
            width: '100%',
            height: { xs: 300, sm: 400, md: 500 },
            position: 'relative',
            overflow: 'hidden',
            backgroundColor: 'background.paper',
          }}
        >
          <Box
            component="img"
            src={post.ogImage}
            alt={post.title}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
          {/* Gradient overlay for better text readability */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '50%',
              background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 100%)',
            }}
          />
        </Box>
      )}

      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs 
          separator={<NavigateNext fontSize="small" />} 
          sx={{ mb: 4 }}
        >
          <Link
            component={RouterLink}
            to="/"
            sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary', textDecoration: 'none' }}
          >
            <Home sx={{ mr: 0.5, fontSize: 18 }} />
            Home
          </Link>
          <Link
            component={RouterLink}
            to="/blog"
            sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary', textDecoration: 'none' }}
          >
            <Article sx={{ mr: 0.5, fontSize: 18 }} />
            Blog
          </Link>
          <Typography color="text.primary" sx={{ fontWeight: 500, maxWidth: 600 }} noWrap>
            {post.title}
          </Typography>
        </Breadcrumbs>

        <Grid container spacing={6}>
          {/* Main Content */}
          <Grid size={{ xs: 12, lg: 8 }}>
            {/* Back to Blog Button */}
            <Button
              component={RouterLink}
              to="/blog"
              startIcon={<ArrowBack />}
              sx={{ mb: 4 }}
              size="small"
            >
              Back to Blog
            </Button>

            {/* Article Header - No Card/Paper wrapper */}
            <Box sx={{ mb: 6 }}>
              {/* Title */}
              <Typography 
                variant="h2" 
                component="h1" 
                sx={{ 
                  fontWeight: 800, 
                  lineHeight: 1.2,
                  mb: 3,
                  fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                }}
              >
                {post.title}
              </Typography>
              
              {/* Excerpt */}
              <Typography 
                variant="h5" 
                sx={{ 
                  color: 'text.secondary', 
                  fontWeight: 400, 
                  lineHeight: 1.6,
                  mb: 4,
                  fontSize: { xs: '1.1rem', sm: '1.25rem' },
                }}
              >
                {post.excerpt}
              </Typography>

              {/* Meta Information */}
              <Stack 
                direction={{ xs: 'column', sm: 'row' }} 
                spacing={3} 
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                divider={<Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />}
                sx={{ mb: 4 }}
              >
                {/* Author(s) with Avatar */}
                <Stack direction="row" spacing={2} alignItems="center">
                  {post.authors.map((authorId, idx) => {
                    const author = authors[authorId];
                    return (
                      <Stack key={authorId} direction="row" spacing={1.5} alignItems="center">
                        <Avatar
                          src={author.image}
                          alt={author.name}
                          sx={{ width: 40, height: 40 }}
                        />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {author.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {author.title.split('|')[0].trim()}
                          </Typography>
                        </Box>
                      </Stack>
                    );
                  })}
                </Stack>

                {/* Date and Reading Time */}
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    {formatDate(post.date)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">•</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {getReadingTime(post)} min read
                  </Typography>
                </Stack>

                {/* Share Actions */}
                <Stack direction="row" spacing={1} alignItems="center">
                  <IconButton onClick={handleShare} size="small" color="primary">
                    <Share fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="primary">
                    <Bookmark fontSize="small" />
                  </IconButton>
                </Stack>
              </Stack>

              {/* Tags */}
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1, mb: 4 }}>
                {post.tags.map((tag, index) => (
                  <Chip
                    key={tag}
                    label={tag}
                    size="small"
                    color={index % 2 === 0 ? 'primary' : 'secondary'}
                    variant="outlined"
                    sx={{ fontWeight: 500 }}
                  />
                ))}
              </Stack>

              <Divider sx={{ mb: 4 }} />
            </Box>

            {/* Article Content - No Paper wrapper, just natural flow */}
            <Box
              sx={{
                '& img': {
                  maxWidth: '100%',
                  height: 'auto',
                  borderRadius: 2,
                  mb: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                },
                '& a': {
                  color: 'primary.main',
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                },
                '& pre': {
                  margin: 0,
                  borderRadius: '8px',
                  overflow: 'hidden',
                },
                '& pre > div': {
                  margin: '0 !important',
                },
                '& code': {
                  fontFamily: 'Consolas, Monaco, "Andale Mono", "Ubuntu Mono", monospace',
                },
              }}
            >
              <ReactMarkdown 
                components={MarkdownComponents(theme)}
                remarkPlugins={[remarkGfm]}
              >
                {post.content}
              </ReactMarkdown>
            </Box>

          {/* Author Bio Section */}
          {post.authors.map((authorId) => {
            const author = authors[authorId];
            return (
              <Paper
                key={authorId}
                elevation={0}
                sx={{
                  p: 4,
                  mt: 4,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                  About the Author
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
                  <Avatar
                    src={author.image}
                    alt={author.name}
                    sx={{ width: 80, height: 80 }}
                    imgProps={{ style: { objectFit: 'contain' } }}
                  />
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      {author.name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'primary.main', mb: 2, fontWeight: 500 }}>
                      {author.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6, mb: 3 }}>
                      {author.bio}
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      {author.social?.linkedin && (
                        <IconButton 
                          href={author.social.linkedin} 
                          target="_blank"
                          size="small"
                          sx={{ color: 'text.secondary', '&:hover': { color: '#0077b5' } }}
                        >
                          <LinkedIn />
                        </IconButton>
                      )}
                      {author.social?.twitter && (
                        <IconButton 
                          href={author.social.twitter} 
                          target="_blank"
                          size="small"
                          sx={{ color: 'text.secondary', '&:hover': { color: '#1da1f2' } }}
                        >
                          <Twitter />
                        </IconButton>
                      )}
                      {author.social?.github && (
                        <IconButton 
                          href={author.social.github} 
                          target="_blank"
                          size="small"
                          sx={{ color: 'text.secondary', '&:hover': { color: 'text.primary' } }}
                        >
                          <GitHub />
                        </IconButton>
                      )}
                      {author.url && (
                        <IconButton 
                          href={author.url} 
                          target="_blank"
                          size="small"
                          sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                        >
                          <Language />
                        </IconButton>
                      )}
                    </Stack>
                  </Box>
                </Box>
              </Paper>
            );
          })}
        </Grid>

        {/* Sidebar */}
        <Grid size={{ xs: 12, lg: 4 }}>
          {/* Related Posts */}
          {relatedPosts.length > 0 && (
            <Paper elevation={0} sx={{ p: 3, mb: 4, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Related Articles
              </Typography>
              <List disablePadding>
                {relatedPosts.map((relatedPost, index) => (
                  <React.Fragment key={relatedPost.slug}>
                    <ListItem disablePadding>
                      <ListItemButton
                        component={RouterLink}
                        to={`/blog/${relatedPost.slug}`}
                        sx={{ px: 0, py: 2, borderRadius: 1 }}
                      >
                        <ListItemText
                          primary={
                            <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.3, mb: 1 }}>
                              {relatedPost.title}
                            </Typography>
                          }
                          secondary={
                            <Stack spacing={1}>
                              <Typography variant="caption" color="text.secondary">
                                {formatDate(relatedPost.date)} • {getReadingTime(relatedPost)} min read
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'text.secondary',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                  lineHeight: 1.3,
                                }}
                              >
                                {relatedPost.excerpt}
                              </Typography>
                            </Stack>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                    {index < relatedPosts.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
              <Button
                component={RouterLink}
                to="/blog"
                variant="outlined"
                fullWidth
                sx={{ mt: 3 }}
              >
                View All Articles
              </Button>
            </Paper>
          )}

          {/* Newsletter/Updates */}
          <Paper 
            elevation={0} 
            sx={{ 
              p: 3, 
              border: '1px solid', 
              borderColor: 'primary.main',
              background: theme => theme.palette.mode === 'dark' 
                ? 'linear-gradient(135deg, rgba(25, 118, 210, 0.1), rgba(66, 165, 245, 0.05))'
                : 'linear-gradient(135deg, rgba(25, 118, 210, 0.05), rgba(66, 165, 245, 0.02))'
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Stay Updated
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
              Get notified about new PWGen-Rust releases, security insights, and development updates.
            </Typography>
            <Stack spacing={2}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                href="https://github.com/KiefStudioMA/PWGen-Rust/releases"
                target="_blank"
                startIcon={<TrendingUp />}
              >
                Follow Releases
              </Button>
              <Button
                variant="outlined"
                fullWidth
                href="https://github.com/KiefStudioMA/PWGen-Rust"
                target="_blank"
                startIcon={<Visibility />}
              >
                Watch on GitHub
              </Button>
            </Stack>
          </Paper>
        </Grid>
        </Grid>

      </Container>
    </Box>
  );
};

export default BlogPostPage;